/**
 * Whether a fragment editor holds work that closing the drawer would throw
 * away.
 *
 * The drawer keeps its whole draft in local state — nothing is written until
 * Save — and it closes from five places (Cancel, the header cross, the mask,
 * Escape and the Back arrow of a stacked drawer). Any of them used to discard
 * an afternoon of Twig without a word, so every close path asks first, and
 * what counts as "work" is decided here rather than at each of them.
 *
 * Sample values are deliberately not part of it: they exist only to make the
 * preview readable and are never persisted, so losing them costs nothing.
 */
export interface FragmentDraftFields {
  /** The block's label. The reference key is server-assigned, so never a draft. */
  name: string;
  content: string;
}

export function hasUnsavedFragmentChanges({
  draft,
  initial,
  translationEdits,
  savedTranslations,
}: {
  draft: FragmentDraftFields;
  /** The form's seed: the record being edited, or empty for a create. */
  initial: FragmentDraftFields;
  /** Wording typed into the translation panel, by full trans key. */
  translationEdits: Record<string, string>;
  /** The partner's persisted wording, by full trans key. */
  savedTranslations: Record<string, string>;
}): boolean {
  if (draft.content !== initial.content) return true;
  if (draft.name !== initial.name) return true;
  /**
   * Translations save on their own (each row has a Save of its own), so an
   * entry here is only unsaved work while it still differs from what the
   * partner has stored — a successful row save drops it from the map, and
   * typing a value back to what it already was is not an edit.
   */
  return Object.entries(translationEdits).some(
    ([key, value]) => value !== (savedTranslations[key] ?? ''),
  );
}
